/**
 * HamburgerMenu - Full-screen navigation drawer (Direction A: spring slide-in)
 *
 * Left-aligned drawer with rounded right edge, scrim overlay,
 * profile section, and navigation items. Replaced the instant
 * `if(!isOpen) return null` pop with an AnimatePresence spring slide (the
 * instant appear was a dated-app tell flagged in the motion audit).
 */

import { useMemo } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { Wand2, Layers, Library, Clapperboard, BarChart3, Settings, X, LogOut } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import type { ViewId } from '@/hooks/useNavigation'
import { useT } from '@/contexts/I18nContext'
import { APP_NAME } from '@shared/constants'

interface HamburgerMenuProps {
  isOpen: boolean
  currentView: ViewId
  onNavigate: (view: ViewId) => void
  onClose: () => void
}

export function HamburgerMenu({ isOpen, currentView, onNavigate, onClose }: HamburgerMenuProps) {
  const { user, isAuthenticated, logout } = useAuth()
  const t = useT()
  const prefersReduced = useReducedMotion()

  const MENU_ITEMS = useMemo<Array<{ view: ViewId; icon: typeof Wand2; label: string; match: ViewId[] }>>(() => [
    // Create hub: one entry for the text/doc/media-import surfaces unified by
    // CreateModeSwitcher. Active across all three creation views.
    { view: 'generate', icon: Wand2, label: t('nav.create.label'), match: ['generate', 'import', 'mediaNew'] },
    { view: 'review', icon: Layers, label: t('nav.reviewSession'), match: ['review'] },
    { view: 'library', icon: Library, label: t('nav.cardLibrary'), match: ['library'] },
    { view: 'media', icon: Clapperboard, label: t('nav.media'), match: ['media'] },
    { view: 'stats', icon: BarChart3, label: t('nav.statistics'), match: ['stats'] },
  ], [t])

  const handleLogout = async () => {
    await logout()
    onClose()
  }

  const displayName = user?.name || user?.email?.split('@')[0] || t('menu.languageLearner')
  const initial = displayName.charAt(0).toUpperCase()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Scrim overlay */}
          <motion.div
            className="fixed inset-0 z-[59] bg-black/40"
            onClick={onClose}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Drawer */}
          <motion.nav
            className="hamburger-menu fixed left-0 top-0 z-[60] h-full w-72 bg-surface-container-lowest rounded-r-[3rem] shadow-2xl flex flex-col"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={prefersReduced ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 34 }}
          >
            {/* Header with close button */}
            <div className="flex items-center justify-between p-6 pb-4">
              <h2 className="font-headline font-extrabold text-lg text-primary">{APP_NAME}</h2>
              <button
                className="p-2 rounded-xl hover:bg-surface-container-low transition-colors"
                onClick={onClose}
                aria-label={t('nav.closeMenu')}
              >
                <X className="w-5 h-5 text-on-surface" />
              </button>
            </div>

            {/* Profile section */}
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                {isAuthenticated && user?.picture ? (
                  <img
                    src={user.picture}
                    alt={displayName}
                    className="w-10 h-10 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center">
                    <span className="font-bold text-sm text-on-primary-container">
                      {isAuthenticated ? initial : '?'}
                    </span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-on-surface truncate">{displayName}</p>
                  {isAuthenticated && user?.email ? (
                    <p className="text-xs text-on-surface-variant truncate">{user.email}</p>
                  ) : (
                    <p className="text-xs text-on-surface-variant">{t('menu.notSignedIn')}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation items */}
            <div className="flex-1 py-4 px-3">
              {MENU_ITEMS.map(({ view, icon: Icon, label, match }) => (
                <button
                  key={view}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left font-semibold text-sm transition-colors ${
                    match.includes(currentView)
                      ? 'bg-primary text-on-primary'
                      : 'text-on-surface-variant hover:bg-surface-container-low'
                  }`}
                  onClick={() => onNavigate(view)}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{label}</span>
                </button>
              ))}

              {/* Divider */}
              <div className="my-3 mx-4 border-t border-outline-variant" />

              {/* Settings */}
              <button
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left font-semibold text-sm transition-colors ${
                  currentView === 'settings'
                    ? 'bg-primary text-on-primary'
                    : 'text-on-surface-variant hover:bg-surface-container-low'
                }`}
                onClick={() => onNavigate('settings')}
              >
                <Settings className="w-5 h-5" strokeWidth={1.5} />
                <span>{t('nav.settings')}</span>
              </button>
            </div>

            {/* Footer */}
            <div className="p-6 pt-4">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left font-semibold text-sm text-error hover:bg-error-container/20 transition-colors"
                >
                  <LogOut className="w-5 h-5" strokeWidth={1.5} />
                  <span>{t('menu.signOut')}</span>
                </button>
              ) : null}
            </div>
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  )
}
